import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import queryString from 'query-string';
import { getLocationSearch } from '../../utils/HistoryUtils';
import { Button, Popconfirm, Tag } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import PaginatedReduxTableWithHeader from '../common/PaginatedReduxTableWithHeader';
import { EmptyState } from '../empty_state/EmptyState';
import { withUserType } from '../withUserType';
import { AGENT_SCENARIOS_CRUD_ACTION_CREATORS } from '../../actions/agent_scenarios_actions';
import { deleteAgentScenario, readAgentScenarios } from '../../thunks/agent_scenarios_thunks';
import { AOPScenario, AOPScope } from '../../types/AOPTypes';
import { AOPScenarioRecord } from '../../thunks/agent_scenarios_thunks';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import { problemTypesRestCrudThunksForBuyer } from '../../thunks/problem_types_thunks';
import { workOrderPrioritiesRestCrudThunksForBuyer } from '../../thunks/work_order_priorities_thunks';
import AOPCreateDrawer from './AOPCreateDrawer';
import { activeWindowLabel } from './utils/AOPScenarioUtils';

const TC_NAME = 'agentScenariosIndex';

/** What this screen puts on the query: the search box, and nothing else. */
interface AOPScenarioQuery {
	search?: string;
	procedure_id?: string;
}

/** What `PaginatedReduxTable` hands its `fetchData`. */
interface AOPTablePagination {
	current: number;
	pageSize: number;
}

interface AOPTableSorting {
	sort_by: string;
	order: 'ascend' | 'descend';
}

/** Only for a refresh before the table has said what page it is on. */
const DEFAULT_PAGINATION = { current: 1, pageSize: 10 };
const PROBLEM_TYPE_TC = 'aopListProblemTypes';
const PRIORITY_TC = 'aopListPriorities';

const SCOPE_SHOWN = 3;

/** `GET /scenarios` sends ids, not names; `#318` stands in until one arrives. */
const ScopeTags: FC<{ scope: AOPScope; allLabel: string; names: { [id: number]: string } }> = ({
	scope,
	allLabel,
	names,
}) => {
	if (scope === 'all') {
		return <Tag>{allLabel}</Tag>;
	}

	const ids = Array.isArray(scope) ? scope : [];

	return (
		<>
			{ids.slice(0, SCOPE_SHOWN).map((id) => (
				<Tag key={id}>{names[id] ? names[id] : `#${id}`}</Tag>
			))}
			{ids.length > SCOPE_SHOWN ? <Tag>+{ids.length - SCOPE_SHOWN}</Tag> : null}
		</>
	);
};

const idsAcross = (records: any[], key: 'problem_type_ids' | 'priority_ids'): number[] => {
	const seen: number[] = [];

	records.forEach((record) => {
		const scope = nullSafeGet(`scenario.${key}`, record);
		if (Array.isArray(scope)) {
			scope.forEach((id) => {
				if (seen.indexOf(id) < 0) {
					seen.push(id);
				}
			});
		}
	});

	return seen;
};

/** `hierarchyName` reads "Inside - Doors", which a bare leaf cannot be told from. */
const namesById = (records: any): { [id: number]: string } => {
	const names: { [id: number]: string } = {};

	Object.keys(records ? records : {}).forEach((id) => {
		const record = records[id];
		const label = record ? record.hierarchyName || record.name : null;

		if (label) {
			names[Number(id)] = label;
		}
	});

	return names;
};

/**
 * The page, and the names its chips need — read here rather than in an effect
 * watching the rows land, and only for ids the store does not already hold.
 *
 * Through `getState`, not a closure: the table keeps the first `fetchData` it
 * is given, so anything captured would be whatever the store held at mount and
 * the check would never find anything.
 */
const readScenariosForList =
	(
		params: AOPScenarioQuery,
		targetCollectionName: string,
		pagination: AOPTablePagination | null,
		sorting: AOPTableSorting | null,
		filters: AOPScenarioQuery | null,
		addToTargetCollection?: boolean
	) =>
	(dispatch, getState): Promise<AOPScenarioRecord[]> =>
		dispatch(
			readAgentScenarios(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection,
				// This table owns the collection: a keystroke in the search box starts a
				// read the next keystroke replaces, and only the newest answer is drawn.
				true
			)
		).then((page: AOPScenarioRecord[]) => {
			const state = getState();

			const unknown = (key: 'problem_type_ids' | 'priority_ids', path: string) => {
				const held = nullSafeGetOrElse(path, state, {});
				return idsAcross(page, key).filter((id) => !held[id]);
			};

			const problemTypeIds = unknown('problem_type_ids', 'problem_types.records');
			const priorityIds = unknown('priority_ids', 'work_order_priorities.records');

			if (problemTypeIds.length > 0) {
				dispatch(problemTypesRestCrudThunksForBuyer.readMultiple(problemTypeIds, PROBLEM_TYPE_TC));
			}

			if (priorityIds.length > 0) {
				dispatch(workOrderPrioritiesRestCrudThunksForBuyer.readMultiple(priorityIds, PRIORITY_TC));
			}

			return page;
		});

const AOPOverviewAllPage: FC<any> = ({
	agentScenarios,
	problemTypeRecords,
	priorityRecords,
	updateFilters,
	clearAndUpdateFilters,
	removeScenario,
	loadScenarios,
	history,
	location,
	userType,
}): React.ReactElement => {
	const [isCreating, setIsCreating] = useState(false);

	const openBuilder = useCallback(
		(procedureId: string) =>
			history.push(
				`/${userType}/workflows/agent_operating_procedures/detail/${procedureId}/procedure`
			),
		[history, userType]
	);

	const onRow = useCallback(
		(record) => ({
			onClick: () => {
				if (record.procedure) {
					openBuilder(record.procedure.id);
				}
			},
		}),
		[openBuilder]
	);

	const createNewButton = (
		<Button
			style={{ marginLeft: 16 }}
			type="primary"
			size="large"
			onClick={() => setIsCreating(true)}
		>
			Create AOP
		</Button>
	);

	const records = getRecordsForTargetCollection(agentScenarios, TC_NAME);

	const problemTypeNames = useMemo(() => namesById(problemTypeRecords), [problemTypeRecords]);
	const priorityNames = useMemo(() => namesById(priorityRecords), [priorityRecords]);

	/**
	 * Rereads the list. `stepBack` is for a delete that empties the page it was
	 * on, which would otherwise be reread as an empty page while earlier ones
	 * are still full.
	 */
	const refreshList = useCallback(
		(stepBack?: boolean) => {
			const collection = agentScenarios ? agentScenarios[TC_NAME] : null;
			const pagination = nullSafeGetOrElse('pagination', collection, DEFAULT_PAGINATION);
			const current = nullSafeGetOrElse('current', pagination, 1);
			const page = stepBack && current > 1 ? { ...pagination, current: current - 1 } : pagination;

			// The table reads its page from the URL on load, so a page changed only
			// in the store comes back on a reload.
			if (page.current !== current) {
				const query = queryString.parse(getLocationSearch(location));
				history.replace(
					`${location.pathname}?${queryString.stringify({ ...query, current: page.current })}`
				);
			}

			loadScenarios(
				nullSafeGetOrElse('filters', collection, {}),
				page,
				nullSafeGetOrElse('sorting', collection, null)
			).catch(() => undefined);
		},
		[agentScenarios, history, loadScenarios, location]
	);

	const columns = useMemo(
		() => [
			{
				title: 'AOP',
				dataIndex: 'procedure',
				// `key` is sent as `sort_by`. The service takes `name`, `created_at`
				// and `updated_at`; `name` is the only one with a column here.
				key: 'name',
				sorter: true,
				render: (procedure) =>
					procedure ? (
						<div className="flex flex-col">
							<div>{procedure.name}</div>
							{procedure.description ? (
								<div className="text-gray-400">{procedure.description}</div>
							) : null}
						</div>
					) : (
						// Creation is two calls and the second can fail. Nothing lists
						// procedures, so this row is the only trace of one.
						<div className="text-gray-400">No procedure linked</div>
					),
			},
			{
				title: 'Scenarios',
				dataIndex: 'scenario',
				render: (scenario: AOPScenario) => (
					<div className="flex flex-wrap items-center gap-2xs">
						<ScopeTags
							scope={scenario.problem_type_ids}
							allLabel="All problem types"
							names={problemTypeNames}
						/>
						<ScopeTags
							scope={scenario.priority_ids}
							allLabel="All priorities"
							names={priorityNames}
						/>
						<Tag>{activeWindowLabel(scenario.active_window)}</Tag>
					</div>
				),
			},
			{
				title: 'Status',
				dataIndex: 'procedure',
				key: 'status',
				// Published or not; which version is live is on the procedure's page.
				render: (procedure) => {
					const version = nullSafeGet('published_version', procedure);
					return version === null || version === undefined ? (
						<div className="text-gray-400">Draft</div>
					) : (
						<div>Published</div>
					);
				},
			},
			{
				title: '',
				key: 'actions',
				width: 64,
				render: (_, record) => (
					// The row opens the builder, so this must not reach it.
					<div onClick={(event) => event.stopPropagation()}>
						<Popconfirm
							title="Delete this procedure and its scenario? Both are removed together."
							okText="Delete"
							okButtonProps={{ danger: true }}
							cancelText="Cancel"
							// Reread: the reducer drops the row but leaves the count the
							// pager reads from.
							onConfirm={() =>
								removeScenario(record.scenario.id)
									.then(() => refreshList(records.length === 1))
									.catch(() => undefined)
							}
						>
							<Button type="text" aria-label="Delete procedure" icon={<DeleteOutlined />} />
						</Popconfirm>
					</div>
				),
			},
		],
		// The names arrive after the rows, so the chips redraw when they do.
		[priorityNames, problemTypeNames, records.length, refreshList, removeScenario]
	);

	return (
		<>
			<PaginatedReduxTableWithHeader
				targetCollectionName={TC_NAME}
				updateFilters={updateFilters}
				stateSlice={agentScenarios}
				clearAndUpdateFilters={clearAndUpdateFilters}
				filterConfig={[]}
				entityCollectionName="agent_scenarios"
				tableColumns={columns}
				onTableRow={onRow}
				fetchData={readScenariosForList}
				rightActions={<div className="flex flex-row items-center gap-2">{createNewButton}</div>}
				showHeader
				emptyState={
					<EmptyState
						graphic={
							<img
								alt="No Data"
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/box.svg"
							/>
						}
						headline={'No procedures yet'}
						body={
							<div style={{ textAlign: 'center' }}>
								<div style={{ marginBottom: 16 }}>
									Create one to tell Radar what to do with a work order.
								</div>
								{createNewButton}
							</div>
						}
					/>
				}
			/>
			{isCreating ? (
				<AOPCreateDrawer
					// A procedure created and then closed away from is in the slice but
					// not in this table's page, so the list has to be reread.
					onClose={(didWrite: boolean) => {
						setIsCreating(false);

						if (didWrite) {
							refreshList();
						}
					}}
					onCreated={(procedureId: string) => {
						setIsCreating(false);
						openBuilder(procedureId);
					}}
				/>
			) : null}
		</>
	);
};

const mapStateToProps = (state) => ({
	agentScenarios: state.agent_scenarios,
	problemTypeRecords: nullSafeGetOrElse('problem_types.records', state, {}),
	priorityRecords: nullSafeGetOrElse('work_order_priorities.records', state, {}),
});

const mapDispatchToProps = (dispatch) => ({
	updateFilters: (filters, targetCollection) =>
		dispatch(AGENT_SCENARIOS_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollection)),
	clearAndUpdateFilters: (filters, targetCollectionName) =>
		dispatch(
			AGENT_SCENARIOS_CRUD_ACTION_CREATORS.clearAndUpdateFilters(filters, targetCollectionName)
		),
	removeScenario: (scenarioId: string) => dispatch(deleteAgentScenario(scenarioId)),
	// The table's own page, sort and filters go back with it, or the list moves
	// under whoever is reading it.
	loadScenarios: (
		filters: AOPScenarioQuery | null,
		pagination: AOPTablePagination | null,
		sorting: AOPTableSorting | null
	) => dispatch(readScenariosForList({}, TC_NAME, pagination, sorting, filters, false)),
});

export default withRouter<any, any>(
	withUserType(connect(mapStateToProps, mapDispatchToProps)(AOPOverviewAllPage))
);
